import EmblaCarousel from 'embla-carousel'

function isVisible(el) {
    if (!el) return false
    const cs = window.getComputedStyle(el)
    return cs.display !== 'none' && cs.visibility !== 'hidden'
}

function setupEmblaNode(node) {
    if (!node || node._emblaSetup) return
    if (!isVisible(node)) return
    node._emblaSetup = true

    let options = { align: 'start', containScroll: 'trimSnaps', loop: false }
    const attr = node.getAttribute('data-embla')
    if (attr) {
        try { options = { ...options, ...JSON.parse(attr) } }
        catch (e) { console.warn('Embla: invalid JSON in data-embla', e, attr) }
    }

    const parseBool = (v, def = false) => {
        if (v == null) return def
        const s = String(v).toLowerCase()
        return s === '1' || s === 'true' || s === 'yes'
    }
    const iconLoop = parseBool(node.getAttribute('data-icon-loop'), false)
    const doCountUp = parseBool(node.getAttribute('data-countup'), false)
    const doAutoplay = parseBool(node.getAttribute('data-autoplay'), false)
    const mobileOnly = (() => {
        const v = node.getAttribute('data-mobile-only')
        if (v == null) return true
        const s = String(v).toLowerCase()
        return !(s === '0' || s === 'false' || s === 'no')
    })()

    const autoplayInterval = (() => {
        const v = node.getAttribute('data-autoplay-interval')
        const n = v ? parseInt(v, 10) : 0
        return Number.isFinite(n) && n > 0 ? n : 3500
    })()

    const slides = Array.from(node.querySelectorAll('.embla__slide'))
    const btnPrev = node.querySelector('[data-embla-prev]')
    const btnNext = node.querySelector('[data-embla-next]')
    const customMq = node.getAttribute('data-embla-mq')
    const mql = window.matchMedia(customMq && customMq.trim()
        ? customMq.trim()
        : '(max-width: 767px)')

    let embla = null
    let desktopHoverBound = false
    let lastMobileSelected = -1

    let autoTimer = null
    let runId = 0
    let countPromise = Promise.resolve()

    node._emblaAutoplayEnabled = false
    node._emblaCountEnabled = false

    if (doAutoplay || doCountUp) {
        const io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    node._emblaAutoplayEnabled = true
                    node._emblaCountEnabled = true
                    if (node._embla) {
                        startCountUp()
                        ++runId
                        clearAuto()
                        scheduleAutoplayAfterCount()
                    }
                } else {
                    node._emblaAutoplayEnabled = false
                    node._emblaCountEnabled = false
                    clearAuto()
                }
            })
        }, { threshold: 0.35 })
        io.observe(node)
        node._emblaIO = io
    }

    const clearAuto = () => { if (autoTimer) { clearTimeout(autoTimer); autoTimer = null } }

    const setActive = () => {
        if (!embla) return
        const i = embla.selectedScrollSnap()
        slides.forEach((el, idx) => el.classList.toggle('is-selected', idx === i))
    }

    const toggleBtns = () => {
        if (!embla) return
        const canPrev = embla.canScrollPrev()
        const canNext = embla.canScrollNext()
        if (btnPrev) { btnPrev.disabled = !canPrev; btnPrev.classList.toggle('is-disabled', !canPrev) }
        if (btnNext) { btnNext.disabled = !canNext; btnNext.classList.toggle('is-disabled', !canNext) }
    }

    const playOnce = (icon, ms = 1200) => {
        if (!icon) return
        icon.setAttribute('trigger', 'hover')
        icon.dispatchEvent(new Event('pointerenter', { bubbles: true }))
        icon.dispatchEvent(new Event('mouseenter', { bubbles: true }))
        clearTimeout(icon._onceTimer)
        icon._onceTimer = setTimeout(() => {
            icon.dispatchEvent(new Event('pointerleave', { bubbles: true }))
            icon.dispatchEvent(new Event('mouseleave', { bubbles: true }))
        }, ms)
    }

    const updateIconsMobile = () => {
        if (iconLoop || !embla) return
        const i = embla.selectedScrollSnap()
        if (i === lastMobileSelected) return
        lastMobileSelected = i

        slides.forEach((slide, idx) => {
            const icon = slide.querySelector('lord-icon')
            if (!icon) return
            if (idx === i) {
                icon.setAttribute('trigger', 'loop')
            } else {
                icon.setAttribute('trigger', 'hover')
                icon.dispatchEvent(new Event('pointerleave', { bubbles: true }))
                icon.dispatchEvent(new Event('mouseleave', { bubbles: true }))
            }
        })
    }

    const resetIconsForDesktop = () => {
        slides.forEach((slide) => {
            const icon = slide.querySelector('lord-icon')
            if (!icon) return
            if (icon._onceTimer) { clearTimeout(icon._onceTimer); icon._onceTimer = null }
            if (iconLoop) icon.setAttribute('trigger', 'loop')
            else {
                icon.setAttribute('trigger', 'hover')
                icon.dispatchEvent(new Event('pointerleave', { bubbles: true }))
                icon.dispatchEvent(new Event('mouseleave', { bubbles: true }))
            }
        })
    }

    const bindDesktopIconHover = () => {
        if (desktopHoverBound || iconLoop) return
        slides.forEach((slide) => {
            const icon = slide.querySelector('lord-icon')
            const card = slide.querySelector('.services__card') || slide
            if (!icon || !card) return
            const enter = () => icon.setAttribute('trigger', 'loop')
            const leave = () => icon.setAttribute('trigger', 'hover')
            card.addEventListener('mouseenter', enter, { passive: true })
            card.addEventListener('mouseleave', leave, { passive: true })
        })
        desktopHoverBound = true
    }

    const parseNumber = (raw) => {
        const s = String(raw).trim()
        const m = s.match(/^(\D*)([\d.,]+)(\D*)$/) || ['', '', '0', '']
        const numStr = m[2].replace(/\./g, '').replace(',', '.')
        const decimals = (numStr.split('.')[1] || '').length
        const value = parseFloat(numStr) || 0
        return { prefix: m[1], value, decimals, suffix: m[3] }
    }
    const formatNumber = (v, d, pre, suf) => {
        const n = d ? v.toFixed(d) : Math.round(v).toString()
        const text = d ? n : Number(n).toLocaleString('pl-PL')
        return `${pre}${text}${suf}`
    }
    const countUp = (el, targetRaw, duration = 3200) => new Promise((resolve) => {
        const { prefix, value, decimals, suffix } = parseNumber(targetRaw)
        const start = performance.now()
        const from = 0
        const ease = (t) => 1 - Math.pow(1 - t, 3)
        if (el._raf) cancelAnimationFrame(el._raf)
        el.textContent = '0'
        const step = (now) => {
            const p = Math.min((now - start) / duration, 1)
            const val = from + (value - from) * ease(p)
            el.textContent = formatNumber(val, decimals, prefix, suffix)
            if (p < 1) el._raf = requestAnimationFrame(step)
            else resolve()
        }
        el._raf = requestAnimationFrame(step)
    })

    const startCountUp = () => {
        if (!embla || !doCountUp || !node._emblaCountEnabled) {
            countPromise = Promise.resolve()
            return
        }
        const idx = embla.selectedScrollSnap()
        const slide = slides[idx]
        const el = slide && slide.querySelector('.js-count')
        if (!el) { countPromise = Promise.resolve(); return }
        const target = el.getAttribute('data-number') || el.textContent
        if (el._raf) cancelAnimationFrame(el._raf)
        countPromise = countUp(el, target, 3200)
    }

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

    const scheduleAutoplayAfterCount = () => {
        if (!doAutoplay || !node._emblaAutoplayEnabled) return
        clearAuto()
        const my = ++runId
        Promise.all([countPromise, sleep(autoplayInterval)]).then(() => {
            if (embla && my === runId) {
                embla.scrollNext()
            }
        })
    }

    const attachArrowHandlersOnce = () => {
        if (btnPrev && !node._prevHandler) {
            node._prevHandler = () => { clearAuto(); ++runId; embla && embla.scrollPrev() }
            btnPrev.addEventListener('click', node._prevHandler)
        }
        if (btnNext && !node._nextHandler) {
            node._nextHandler = () => { clearAuto(); ++runId; embla && embla.scrollNext() }
            btnNext.addEventListener('click', node._nextHandler)
        }
    }
    const detachArrowHandlers = () => {
        if (btnPrev && node._prevHandler) { btnPrev.removeEventListener('click', node._prevHandler); node._prevHandler = null }
        if (btnNext && node._nextHandler) { btnNext.removeEventListener('click', node._nextHandler); node._nextHandler = null }
    }

    const initEmblaInstance = () => {
        if (embla) return
        embla = EmblaCarousel(node, options)
        node._embla = embla

        attachArrowHandlersOnce()

        node._emblaUserTouched = false
        const markTouched = () => { node._emblaUserTouched = true }
        embla.on('pointerDown', markTouched)

        const forceStartAtZero = () => {
            if (!node._emblaUserTouched && embla) embla.scrollTo(0, true)
        }

        embla.on('pointerDown', () => { clearAuto(); ++runId })

        embla.on('select', () => {
            setActive()
            toggleBtns()
            updateIconsMobile()
            ++runId
            clearAuto()
            startCountUp()
            scheduleAutoplayAfterCount()
        })

        embla.on('settle', () => {
            scheduleAutoplayAfterCount()
        })

        embla.on('reInit', () => {
            setActive()
            toggleBtns()
            updateIconsMobile()
            ++runId
            clearAuto()
            startCountUp()
            scheduleAutoplayAfterCount()
            forceStartAtZero()
        })

        lastMobileSelected = -1
        setActive()
        toggleBtns()
        updateIconsMobile()
        ++runId
        clearAuto()
        startCountUp()
        scheduleAutoplayAfterCount()
        requestAnimationFrame(forceStartAtZero)
    }

    const destroyEmblaInstance = () => {
        if (!embla) return
        clearAuto()
        embla.destroy()
        embla = null
        node._embla = null
        detachArrowHandlers()
        slides.forEach((s) => s.classList.remove('is-selected'))
        if (btnPrev) { btnPrev.disabled = false; btnPrev.classList.remove('is-disabled') }
        if (btnNext) { btnNext.disabled = false; btnNext.classList.remove('is-disabled') }
        slides.forEach((s) => { const el = s.querySelector('.js-count'); if (el && el._raf) cancelAnimationFrame(el._raf) })
    }

    const apply = (mqEvent) => {
        if (mobileOnly) {
            if (mqEvent.matches) initEmblaInstance()
            else {
                destroyEmblaInstance()
                resetIconsForDesktop()
                bindDesktopIconHover()
            }
        } else {
            if (!embla) initEmblaInstance()
        }
    }

    apply(mql)
    if (mobileOnly) {
        if (mql.addEventListener) mql.addEventListener('change', apply)
        else mql.addListener(apply)
    }
}

function initEmbla(root = document) {
    root.querySelectorAll('[data-embla]').forEach((n) => {
        if (isVisible(n)) setupEmblaNode(n)
    })
}

function initEmblaFor(scope) {
    if (!scope) return
    scope.querySelectorAll('[data-embla]').forEach((n) => {
        if (!n._embla && isVisible(n)) setupEmblaNode(n)
    })
}

function destroyEmblaFor(scope) {
    if (!scope) return
    scope.querySelectorAll('[data-embla]').forEach((n) => {
        if (n._autoTimer) { clearTimeout(n._autoTimer); n._autoTimer = null }

        const prevBtn = n.querySelector('[data-embla-prev]')
        const nextBtn = n.querySelector('[data-embla-next]')
        if (prevBtn && n._prevHandler) {
            prevBtn.removeEventListener('click', n._prevHandler)
            n._prevHandler = null
        }
        if (nextBtn && n._nextHandler) {
            nextBtn.removeEventListener('click', n._nextHandler)
            n._nextHandler = null
        }

        if (n._embla) {
            try { n._embla.destroy() } catch (e) { }
            n._embla = null
        }

        if (n._emblaIO) { try { n._emblaIO.disconnect() } catch (e) { } n._emblaIO = null }
        n._emblaAutoplayEnabled = false
        n._emblaCountEnabled = false

        n._emblaSetup = false
        n._emblaUserTouched = false

        if (prevBtn) { prevBtn.disabled = false; prevBtn.classList.remove('is-disabled') }
        if (nextBtn) { nextBtn.disabled = false; nextBtn.classList.remove('is-disabled') }

        n.querySelectorAll('.embla__slide.is-selected').forEach(s => s.classList.remove('is-selected'))
    })
}

function initTLLogos() {
    const containers = document.querySelectorAll('[data-tl-logos-auto]')

    containers.forEach(container => {
        if (container._tlLogosInit) return
        container._tlLogosInit = true

        const logosWrap = container.querySelector('.tl__logos')
        const track = container.querySelector('.tl__logos-track')
        const allLogos = Array.from(container.querySelectorAll('.tl__logo'))

        if (!logosWrap || !track || allLogos.length === 0) return

        const originalCount = allLogos.length / 3

        let embla = null
        let autoTimer = null
        let runId = 0
        let currentMode = null

        const checkAndInit = () => {
            const images = allLogos.map(l => l.querySelector('img')).filter(Boolean)
            const allLoaded = images.every(img => img.complete && img.naturalWidth > 0)

            if (!allLoaded) {
                setTimeout(checkAndInit, 100)
                return
            }

            allLogos.forEach(logo => logo.style.display = '')

            requestAnimationFrame(() => {
                const originalLogos = allLogos.slice(0, originalCount)
                const currentGap = window.innerWidth >= 768 ? 60 : 24

                let availableWidth

                if (window.innerWidth < 768) {
                    availableWidth = window.innerWidth - 48
                } else if (window.innerWidth < 1200) {
                    const containerInner = container.querySelector('.tl__inner')
                    availableWidth = containerInner ? containerInner.offsetWidth : window.innerWidth - 48
                } else {
                    const containerInner = container.querySelector('.tl__inner')
                    availableWidth = containerInner ? containerInner.offsetWidth : 1140
                }

                let totalLogosWidth = 0
                originalLogos.forEach((logo, idx) => {
                    totalLogosWidth += logo.offsetWidth
                    if (idx < originalLogos.length - 1) {
                        totalLogosWidth += currentGap
                    }
                })

                console.log('📊 Available:', availableWidth, 'Total:', totalLogosWidth, 'Gap:', currentGap)

                const needsCarousel = totalLogosWidth > availableWidth

                if (needsCarousel && currentMode !== 'carousel') {
                    console.log('🎠 Carousel ON')
                    initCarousel()
                } else if (!needsCarousel && currentMode !== 'static') {
                    console.log('📍 Static ON')
                    initStatic()
                }
            })
        }

        const initCarousel = () => {
            if (embla) {
                embla.destroy()
                embla = null
                container._embla = null
            }

            if (container._tlIO) {
                container._tlIO.disconnect()
                container._tlIO = null
            }

            currentMode = 'carousel'
            container.classList.add('is-carousel')
            container.classList.remove('is-static')

            allLogos.forEach(logo => logo.style.display = '')

            logosWrap.classList.add('embla')
            track.classList.add('embla__container')
            allLogos.forEach(logo => logo.classList.add('embla__slide'))

            embla = EmblaCarousel(logosWrap, {
                align: 'start',
                containScroll: 'trimSnaps',
                loop: true,
                dragFree: false
            })

            container._embla = embla

            const clearAuto = () => {
                if (autoTimer) {
                    clearTimeout(autoTimer)
                    autoTimer = null
                }
            }

            const scheduleNext = () => {
                clearAuto()
                const myRun = ++runId
                autoTimer = setTimeout(() => {
                    if (embla && myRun === runId) {
                        embla.scrollNext()
                    }
                }, 3000)
            }

            embla.on('select', scheduleNext)
            embla.on('settle', scheduleNext)
            embla.on('pointerDown', () => {
                clearAuto()
                ++runId
            })

            const io = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        scheduleNext()
                    } else {
                        clearAuto()
                    }
                })
            }, { threshold: 0.35 })

            io.observe(container)
            container._tlIO = io

            scheduleNext()
        }

        const initStatic = () => {
            if (embla) {
                embla.destroy()
                embla = null
                container._embla = null
            }

            if (container._tlIO) {
                container._tlIO.disconnect()
                container._tlIO = null
            }

            if (autoTimer) {
                clearTimeout(autoTimer)
                autoTimer = null
            }

            currentMode = 'static'
            container.classList.add('is-static')
            container.classList.remove('is-carousel')

            logosWrap.classList.remove('embla')
            track.classList.remove('embla__container')
            allLogos.forEach(logo => logo.classList.remove('embla__slide'))

            allLogos.forEach((logo, idx) => {
                if (idx < originalCount) {
                    logo.style.display = ''
                } else {
                    logo.style.display = 'none'
                }
            })
        }

        checkAndInit()

        let resizeTimer
        const handleResize = () => {
            clearTimeout(resizeTimer)
            resizeTimer = setTimeout(() => {
                currentMode = null

                allLogos.forEach(logo => {
                    logo.style.display = ''
                    logo.classList.remove('embla__slide')
                })
                logosWrap.classList.remove('embla')
                track.classList.remove('embla__container')
                container.classList.remove('is-carousel', 'is-static')

                checkAndInit()
            }, 250)
        }

        window.addEventListener('resize', handleResize)
    })
}

document.addEventListener('DOMContentLoaded', () => {
    initEmbla()
    initTLLogos()
})

window.lpEmbla = { initEmbla, initEmblaFor, destroyEmblaFor, initTLLogos }
